import { mediaUrl } from '@/lib/mediaUrl';
import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Check, RotateCcw } from 'lucide-react';

const LabelDiagramElement = ({ config, element, onComplete, onSaveData, isCompleted }) => {
  const lc = config || {};
  const labels = lc.labels || [];
  const [placed, setPlaced] = useState({});
  const [checked, setChecked] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [shuffledLabels] = useState(() => labels.map(l => l.text).sort(() => Math.random() - 0.5));
  const imageRef = useRef(null);

  const placedTexts = Object.values(placed);
  const availableLabels = shuffledLabels.filter(t => !placedTexts.includes(t));

  const handleImageClick = (e) => {
    if (!selectedLabel || !imageRef.current) return;
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const nearest = labels.find(l => !placed[l.id] && Math.abs(l.x - x) < 15 && Math.abs(l.y - y) < 15);
    if (nearest) { setPlaced(prev => ({ ...prev, [nearest.id]: selectedLabel })); setSelectedLabel(null); }
  };

  const handleCheck = () => { setChecked(true); const allCorrect = labels.every(l => placed[l.id] === l.text); onSaveData({ placed, correct: allCorrect }); if (allCorrect && !isCompleted) { onComplete(); toast.success('All labels placed correctly!'); } };

  return (
    <div className="space-y-4" data-testid="label-diagram-element">
      {lc.instructions && <p className="text-sm text-slate-600 italic">{lc.instructions}</p>}
      <div className="flex flex-wrap gap-2">
        {availableLabels.map(text => (
          <button key={text} onClick={() => setSelectedLabel(selectedLabel === text ? null : text)} className={`px-3 py-1.5 rounded-lg text-sm font-medium border-2 transition-colors ${selectedLabel === text ? 'border-[#20acf6] bg-[#20acf6]/10 text-[#20acf6]' : 'border-slate-200 bg-white hover:border-slate-300'}`}>{text}</button>
        ))}
      </div>
      <div className="relative border-2 border-slate-200 rounded-xl overflow-hidden cursor-crosshair" ref={imageRef} onClick={handleImageClick}>
        {lc.image_url ? <img src={mediaUrl(lc.image_url)} alt={lc.image_alt || "Diagram"} className="w-full" /> : <div className="w-full h-64 bg-slate-100 flex items-center justify-center text-slate-400">No diagram image configured</div>}
        {labels.map(l => {
          const isPlaced = placed[l.id]; const isCorrect = checked && placed[l.id] === l.text; const isWrong = checked && placed[l.id] && placed[l.id] !== l.text;
          return (<div key={l.id} className={`absolute transform -translate-x-1/2 -translate-y-1/2 px-2 py-1 rounded text-xs font-bold border-2 ${isCorrect ? 'border-green-500 bg-green-100 text-green-800' : isWrong ? 'border-red-500 bg-red-100 text-red-800' : isPlaced ? 'border-[#20acf6] bg-blue-100 text-[#20acf6]' : 'border-dashed border-slate-400 bg-white/80 text-slate-400'}`} style={{ left: `${l.x}%`, top: `${l.y}%` }}>{isPlaced ? placed[l.id] : '?'}</div>);
        })}
      </div>
      {selectedLabel && <p className="text-xs text-[#20acf6] font-medium">Click on the diagram to place: {selectedLabel}</p>}
      {!checked && Object.keys(placed).length === labels.length ? (<Button onClick={handleCheck} className="bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="check-labels-btn"><Check className="h-4 w-4 mr-2" /> Check Labels</Button>) : checked && !labels.every(l => placed[l.id] === l.text) && (<Button onClick={() => { setChecked(false); setPlaced({}); }} variant="outline" data-testid="retry-labels-btn"><RotateCcw className="h-4 w-4 mr-2" /> Try Again</Button>)}
    </div>
  );
};

export default LabelDiagramElement;
